import React from "react";
import { Box, Card, Stack, Switch, Typography } from "@mui/material";
import { TrendingUp, TrendingDown, TrendingFlat } from "@mui/icons-material";

import "../../styles/animations.css";

export default function UsageMetric(props) {
  const { title, value, unit, icon, color, secondaryValue, subtitle, yesterdayValue, showComparison, isTemperature, alertColor, isVanneThermo, phaseInfo, relayControl } = props;
  const isMobile = window.innerWidth < 900;
  
  // Calculate variation if comparison is enabled
  const calculateVariation = () => {
    if (!showComparison || yesterdayValue === undefined || yesterdayValue === null) return null;
    
    const current = parseFloat(value) || 0;
    const yesterday = parseFloat(yesterdayValue) || 0;
    
    if (yesterday === 0) {
      if (current > 0) return { value: current, percentage: 100, trend: "up" };
      return { value: 0, percentage: 0, trend: "flat" };
    }
    
    const diff = current - yesterday;
    const percentage = Math.round((diff / yesterday) * 100);
    const trend = diff > 0 ? "up" : diff < 0 ? "down" : "flat";

    return { value: diff, percentage, trend };
  };
  
  const variation = calculateVariation();
  
  const getTrendIcon = (trend) => {
    const iconStyle = { fontSize: isMobile ? 16 : 20, ml: 0.5 };
    switch (trend) {
      case "up":
        return <TrendingUp sx={{ ...iconStyle, color: "#ef4444" }} />;
      case "down":
        return <TrendingDown sx={{ ...iconStyle, color: "#22c55e" }} />;
      default:
        return <TrendingFlat sx={{ ...iconStyle, color: "#6b7280" }} />;
    }
  };

  
  // Couleur du liseré selon le type de métrique.
  // En light mode, le handoff Paper & Sage privilégie un liseré ambre sobre.
  // CSS variables `--kpi-rail-*` sont définies par thème dans variables.css.
  const railColor = isVanneThermo
    ? "var(--kpi-rail-trv)"
    : (title?.toLowerCase().includes("électricité") || unit?.toLowerCase().includes("kwh"))
      ? "var(--kpi-rail-elec)"
      : (title?.toLowerCase().includes("température") || unit?.toLowerCase().includes("°c"))
        ? "var(--kpi-rail-temp)"
        : "var(--kpi-rail-water)";

  return (
    <Card sx={{
      display: "flex",
      flexFlow: "column",
      position: "relative",
      borderRadius: "var(--radius-tile, 14px) !important",
      border: "1px solid var(--border-light)",
      boxShadow: "var(--shadow-sm)",
      backgroundColor: "var(--bg-card)",
      transition: "transform 180ms ease, box-shadow 180ms ease",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "var(--shadow-md)",
      },
      ...(isTemperature && isMobile && { minHeight: "130px" })
    }}>
      {/* Liseré 3px en haut */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        backgroundColor: railColor,
        zIndex: 1,
        borderTopLeftRadius: "inherit",
        borderTopRightRadius: "inherit",
      }}></div>

      {/* Badge d'alerte rouge — pill pleine */}
      {alertColor === "red" && (
        <div style={{
          position: "absolute",
          top: isMobile ? "6px" : "8px",
          right: isMobile ? "6px" : "8px",
          fontSize: isMobile ? "10px" : "11px",
          fontWeight: 700,
          letterSpacing: "0.2px",
          color: "var(--panel-alert-fg)",
          backgroundColor: "var(--panel-alert-bg)",
          animation: "pulse 1.5s infinite",
          padding: isMobile ? "2px 6px" : "3px 8px",
          borderRadius: "4px",
          zIndex: 2,
          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
        }}>
          Alert
        </div>
      )}
      {/* Badge warn — triangle nu, sans rectangle */}
      {alertColor === "yellow" && (
        <div style={{
          position: "absolute",
          top: isMobile ? "4px" : "6px",
          right: isMobile ? "4px" : "6px",
          fontSize: isMobile ? "16px" : "18px",
          lineHeight: 1,
          zIndex: 2,
          filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.15))",
        }}>
          ⚠️
        </div>
      )}
      {/* Toggle relais (ex: ADE_DRY associé à un canal UC300) — coin haut droit
          Label ON/OFF à gauche du switch, couleur verte (ON) / rouge (OFF). */}
      {relayControl && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            top: isMobile ? "2px" : "4px",
            right: alertColor ? (isMobile ? "32px" : "44px") : (isMobile ? "4px" : "6px"),
            zIndex: 3,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: isMobile ? "0.7rem" : "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.5px",
              color: relayControl.state ? "#22c55e" : "#ef4444",
              userSelect: "none",
            }}
          >
            {relayControl.state ? "ON" : "OFF"}
          </Typography>
          <Switch
            size="small"
            checked={!!relayControl.state}
            disabled={!!relayControl.disabled}
            onChange={(e) => relayControl.onToggle(e.target.checked ? 1 : 0)}
            sx={{
              "& .MuiSwitch-switchBase": {
                color: "#ef4444",
              },
              "& .MuiSwitch-switchBase + .MuiSwitch-track": {
                backgroundColor: "#ef4444",
                opacity: 0.5,
              },
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#22c55e",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#22c55e",
                opacity: 0.5,
              },
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          padding: isMobile ? 0.5 : 1,
        }}>
        <Box>
          <Typography sx={{
            display: "flex",
            alignItems: "center",
            fontFamily: "var(--font-display)",
            fontWeight: 600,
            fontSize: isMobile ? "1rem" : "1.125rem",
            color: "var(--text-primary)",
            letterSpacing: "-0.01em",
          }}>
            {isMobile && icon && (
              <Box sx={{ mr: 0.5, display: "flex", alignItems: "center" }}>
                {React.cloneElement(icon, {
                  size: 20,
                  sx: { ...icon.props.sx, fontSize: 20 }
                })}
              </Box>
            )}
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      <Stack
        spacing={2}
        direction={"row"}
        justifyContent={isMobile ? "flex-end" : "space-between"}
        alignItems="center"
        sx={{
          p: isMobile ? 1 : 2,
          pt: 0,
          //full height of the card
          flex: "1 1 auto",
        }}
      >
        {!isMobile && icon}
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              flexDirection: "row",
            }}
          >
            <Typography
              sx={{
                fontFamily: "var(--font-screen)",
                fontSize: isMobile ? "1.5rem" : "1.875rem",
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: "0.01em",
                paddingRight: "0.3rem",
                textAlign: "right",
                color: "var(--text-primary)",
              }}
            >
              {value}
            </Typography>
            <Typography
              sx={{
                fontFamily: "var(--font-sans)",
                fontSize: isMobile ? "0.85rem" : "0.875rem",
                fontWeight: 500,
                alignSelf: "flex-end",
                lineHeight: 1.2,
                textAlign: "right",
                color: "var(--text-muted)",
              }}
            >
              {/* Affichage L (m³) pour litres >= 1000, sinon unité normale */}
              {(unit?.toLowerCase().includes('litre') && parseFloat(String(value).replace(/\s/g, '')) >= 1000) ? (
                <>
                  L <span style={{
                    fontSize: isMobile ? "0.85rem" : "1.1rem",
                    color: "var(--text-secondary)",
                  }}>({(parseFloat(String(value).replace(/\s/g, '')) / 1000).toFixed(2).replace('.', ',')} m³)</span>
                </>
              ) : unit}
            </Typography>
          </Box>
          
          {/* Comparison with yesterday */}
          {showComparison && variation && (
            <Box sx={{ 
              display: "flex", 
               
              justifyContent: "flex-end", 
              mt: 0.5,
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-end" : "center"
            }}>
              {isMobile ? (
                // Version mobile sur 2 lignes
                <>
                  <Typography variant="body2" sx={{ 
                    color: "text.secondary",
                    textAlign: "right",
                    fontSize: "0.95rem"
                  }}>
                    Période préc.: {typeof yesterdayValue === "number" ? (unit.toLowerCase() === "kwh" ? yesterdayValue.toFixed(2) : Math.round(yesterdayValue)) : yesterdayValue} {unit}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    fontWeight: "bold", 
                    display: "flex", 
                    alignItems: "center",
                    justifyContent: "flex-end",
                    color: variation.trend === "up" ? "error.main" : variation.trend === "down" ? "success.main" : "text.secondary"
                  }}>
                    {variation.value > 0 ? '+' : ''}{variation.value.toFixed(unit.toLowerCase() === "kwh" ? 2 : 0)} {unit}({variation.percentage}%)
                    {getTrendIcon(variation.trend)}
                  </Typography>
                </>
              ) : (
                // Version desktop actuelle (sur une ligne)
                <>
                  <Typography variant="body1" sx={{ color: "text.secondary", mr: 1 }}>
                    Période préc.: {typeof yesterdayValue === "number" ? (unit.toLowerCase() === "kwh" ? yesterdayValue.toFixed(2) : Math.round(yesterdayValue)) : yesterdayValue} {unit}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "bold", display: "flex", alignItems: "center", color: variation.trend === "up" ? "error.main" : variation.trend === "down" ? "success.main" : "text.secondary" }}>
                    {variation.value > 0 ? '+' : ''}{variation.value.toFixed(unit.toLowerCase() === "kwh" ? 2 : 0)} {unit}
                    ({variation.percentage}%)
                    {getTrendIcon(variation.trend)}
                  </Typography>
                </>
              )}
            </Box>
          )}
          
          {secondaryValue && (
            <Typography
              sx={{
                fontSize: isMobile ? "1rem" : "1.5rem",
                ml: 1.2,
                textAlign: "right",
                mt: showComparison ? 0.5 : 0,
              }}
            >
              {secondaryValue}
            </Typography>
          )}
          {phaseInfo && (
            <Box sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.5,
              mt: 0.5,
              pt: 0.5,
              borderTop: "1px solid var(--border-subtle)",
            }}>
              <Typography sx={{
                fontSize: isMobile ? "0.75rem" : "0.9rem",
                color: phaseInfo.color || "var(--text-secondary)",
                fontWeight: 500,
              }}>
                {phaseInfo.voltage !== undefined && phaseInfo.voltage !== null
                  ? `${phaseInfo.voltage} V`
                  : "— V"}
              </Typography>
              <Box sx={{
                width: "1px",
                height: "14px",
                backgroundColor: "var(--border-subtle)",
              }} />
              <Typography sx={{
                fontSize: isMobile ? "0.75rem" : "0.9rem",
                color: phaseInfo.color || "var(--text-secondary)",
                fontWeight: 500,
              }}>
                {phaseInfo.current !== undefined && phaseInfo.current !== null
                  ? `${phaseInfo.current} A`
                  : "— A"}
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Card>
  );
}
